import { DAY_MS } from "@amber/contracts";
import type { TASK_TYPES } from "./records.js";

type TaskType = (typeof TASK_TYPES)[number];

export const SEVERITIES = ["s1", "s2", "s3"] as const;
export type Severity = (typeof SEVERITIES)[number];

export interface SeverityInfo {
  readonly code: "S1" | "S2" | "S3";
  readonly label: string;
  readonly meaning: string;
  readonly containment: string;
}

/** The severity table of `docs/operations/runbooks.md` (QO-14). */
export const SEVERITY_INFO: Record<Severity, SeverityInfo> = {
  s1: {
    code: "S1",
    label: "Evidence at risk",
    meaning: "Evidence may be wrong or lost, or a restriction may not hold",
    containment: "Eligibility suspended, task opened and consumers notified automatically",
  },
  s2: {
    code: "S2",
    label: "Coverage incomplete",
    meaning: "Coverage is silently incomplete",
    containment: "Gap recorded with reason and next step; work held or moved to the next route",
  },
  s3: {
    code: "S3",
    label: "Work delayed",
    meaning: "Work is delayed but nothing is lost",
    containment: "Work waits with a visible reason; nothing consumes attempts while waiting",
  },
};

const BY_CAUSE: ReadonlyMap<string, Severity> = new Map([
  ["integrity:archive", "s1"],
  ["alert:integrity_failures", "s1"],
  ["alert:identity_merge_rate", "s1"],
  ["alert:stuck_work", "s3"],
]);

const BY_TYPE: Partial<Record<TaskType, Severity>> = {
  coverage_gap: "s2",
  route: "s2",
  platform_issue: "s2",
  failed_items: "s3",
  source_access: "s3",
  operational_failure: "s3",
  budget: "s3",
};

const DELAY_ONLY_PLATFORM_CAUSES = /^platform_issue:[^:]+:(quota|rate_limit)$/;

// QO-14: derived on read so a change to the runbook table applies to open tasks; an opener that knows more (a reported
// wrong speaker) stores its own.
export function taskSeverity(type: TaskType, causeKey: string, stored: Severity | null = null): Severity | null {
  if (stored) return stored;
  const byCause = BY_CAUSE.get(causeKey);
  if (byCause) return byCause;
  if (DELAY_ONLY_PLATFORM_CAUSES.test(causeKey)) return "s3";
  return BY_TYPE[type] ?? null;
}

export const DRILL_KINDS = ["restore", "dependency_failure"] as const;
export type DrillKind = (typeof DRILL_KINDS)[number];
export const DRILL_KIND_LABELS: Record<DrillKind, string> = {
  restore: "Restore",
  dependency_failure: "Dependency failure",
};
export const DRILL_OUTCOMES = ["passed", "failed"] as const;
export type DrillOutcome = (typeof DRILL_OUTCOMES)[number];

/** QO-14 "at least quarterly": days after the last passed drill of a kind until it is due again. */
export const DRILL_INTERVAL_DAYS = 90;

export interface DrillRecord {
  readonly kind: DrillKind;
  readonly performedOn: Date;
  readonly outcome: DrillOutcome;
}

export interface DrillDue {
  readonly kind: DrillKind;
  readonly lastPassed: Date | null;
  readonly lastFailed: Date | null;
}

function latest(drills: readonly DrillRecord[], kind: DrillKind, outcome: DrillOutcome): Date | null {
  const times = drills.filter((d) => d.kind === kind && d.outcome === outcome).map((d) => d.performedOn.getTime());
  return times.length === 0 ? null : new Date(Math.max(...times));
}

export function drillsDue(drills: readonly DrillRecord[], now: Date): DrillDue[] {
  return DRILL_KINDS.flatMap((kind) => {
    const lastPassed = latest(drills, kind, "passed");
    if (lastPassed && now.getTime() < lastPassed.getTime() + DRILL_INTERVAL_DAYS * DAY_MS) return [];
    const lastFailed = latest(drills, kind, "failed");
    return [
      { kind, lastPassed, lastFailed: lastFailed && (!lastPassed || lastFailed > lastPassed) ? lastFailed : null },
    ];
  });
}
